import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useAppAuth } from '@/contexts/app-auth.context'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { ChartColumn } from 'lucide-react'
import {
  adminPublicApiClient,
  companyPublicApiClient,
  type PlatformCompanyUsage,
} from '@/lib/api/admin-public-api.client'
import { SectionTabs, formatTimestamp } from './platform-shared'
import { ROUTE_REGISTRY } from '@/lib/routes'

type UsageWindow = '7' | '30'

const successRate = (succeeded: number, total: number) =>
  total === 0 ? '—' : `${Math.round((succeeded / total) * 100)}%`

function StatCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: number
  hint: string
  tone?: string
}) {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className={cn('mt-2 text-4xl font-semibold', value > 0 && tone)}>
          {value.toLocaleString()}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
      </CardContent>
    </Card>
  )
}

const shareOfEvents = (part: number, total: number) =>
  total === 0 ? '\u2014' : `${Math.round((part / total) * 100)}% of events`

export function ApiUsagePage() {
  const { isInitialized, currentCompany, isSuperAdmin } = useAppAuth()
  const companyId = currentCompany?.id
  const [usageWindow, setUsageWindow] = useState<UsageWindow>('7')
  const [usage, setUsage] = useState<PlatformCompanyUsage | null>(null)
  const [loading, setLoading] = useState(false)
  // Guards against a slow response for a previous selection landing after a
  // newer one and overwriting it
  const requestSeq = useRef(0)

  const loadUsage = useCallback(async () => {
    if (!companyId) {
      setUsage(null)
      return
    }
    const requestId = ++requestSeq.current
    try {
      setLoading(true)
      // Super admins read via the cross-company admin API for the company
      // selected in the app switcher; company users read their own company
      const result = isSuperAdmin
        ? await adminPublicApiClient.getCompanyUsage(companyId, usageWindow)
        : await companyPublicApiClient.getUsage(usageWindow)
      if (requestId !== requestSeq.current) return
      setUsage(result)
    } catch (error) {
      if (requestId !== requestSeq.current) return
      toast.error(
        error instanceof Error ? error.message : 'Failed to load usage',
      )
      setUsage(null)
    } finally {
      if (requestId === requestSeq.current) setLoading(false)
    }
  }, [companyId, isSuperAdmin, usageWindow])

  useEffect(() => {
    if (!isInitialized) return
    loadUsage()
  }, [isInitialized, loadUsage])

  if (!isInitialized) return null

  return (
    <div className="h-full overflow-y-auto mt-16">
      <div className="space-y-6 p-6 pt-0">
        <div className="mt-6">
          <PageHeader title="Webhooks" />
        </div>
        <SectionTabs
          tabs={[
            { label: 'Usage', to: ROUTE_REGISTRY.WEBHOOKS.path, active: true },
            {
              label: 'Endpoints',
              to: ROUTE_REGISTRY.WEBHOOKS_ENDPOINTS.path,
              active: false,
            },
            {
              label: 'Deliveries',
              to: ROUTE_REGISTRY.WEBHOOKS_DELIVERIES.path,
              active: false,
            },
          ]}
          actions={
            <Select
              value={usageWindow}
              onValueChange={value => setUsageWindow(value as UsageWindow)}
            >
              <SelectTrigger className="h-8 w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          }
        />

        {!companyId ? (
          <Card className="w-full">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <ChartColumn className="h-8 w-8 text-gray-400 mb-4" />
              <h3 className="mb-2 text-lg font-semibold">Pick a company</h3>
              <p className="max-w-sm text-sm text-muted-foreground">
                Select a company in the switcher to see its events, API keys,
                and webhook deliveries.
              </p>
            </CardContent>
          </Card>
        ) : loading && usage === null ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[...Array(4)].map((_, index) => (
                <Skeleton key={index} className="h-20 w-full" />
              ))}
            </div>
            <Skeleton className="h-40 w-full" />
          </div>
        ) : usage === null ? null : (
          <div
            className={cn(
              'space-y-6 transition-opacity duration-200',
              loading && 'pointer-events-none opacity-50',
            )}
          >
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
              <StatCard
                label="Events"
                value={usage.events.total}
                hint={`last ${usage.windowDays} days`}
              />
              <StatCard
                label="Accepted"
                value={usage.events.accepted}
                hint={shareOfEvents(usage.events.accepted, usage.events.total)}
                tone="text-emerald-700 dark:text-emerald-400"
              />
              <StatCard
                label="Rejected"
                value={usage.events.rejected}
                hint={shareOfEvents(usage.events.rejected, usage.events.total)}
                tone="text-amber-700 dark:text-amber-400"
              />
              <StatCard
                label="Failed"
                value={usage.events.failed}
                hint={shareOfEvents(usage.events.failed, usage.events.total)}
                tone="text-red-700 dark:text-red-400"
              />
            </div>

            <div className="grid gap-6 xl:grid-cols-2">
              <Card className="w-full">
                <CardContent className="p-0">
                  <div className="border-b px-6 py-3 text-sm font-medium">
                    By event
                  </div>
                  {usage.byEvent.length === 0 ? (
                    <p className="px-6 py-8 text-sm text-muted-foreground">
                      No events in this window.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-6">Event</TableHead>
                          <TableHead className="w-20">Total</TableHead>
                          <TableHead className="w-20">Accepted</TableHead>
                          <TableHead className="w-20">Rejected</TableHead>
                          <TableHead className="w-20 pr-6">Failed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usage.byEvent.map(row => (
                          <TableRow key={row.event}>
                            <TableCell className="pl-6 font-mono text-sm">
                              {row.event}
                            </TableCell>
                            <TableCell className="font-medium tabular-nums">
                              {row.total}
                            </TableCell>
                            <TableCell
                              className={cn(
                                'tabular-nums',
                                row.accepted === 0 &&
                                  'text-muted-foreground/60',
                              )}
                            >
                              {row.accepted}
                            </TableCell>
                            <TableCell
                              className={cn(
                                'tabular-nums',
                                row.rejected === 0 &&
                                  'text-muted-foreground/60',
                              )}
                            >
                              {row.rejected}
                            </TableCell>
                            <TableCell
                              className={cn(
                                'tabular-nums',
                                row.failed === 0 && 'text-muted-foreground/60',
                              )}
                            >
                              {row.failed}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
              <Card className="w-full">
                <CardContent className="p-0">
                  <div className="border-b px-6 py-3 text-sm font-medium">
                    API keys
                  </div>
                  {usage.keys.length === 0 ? (
                    <p className="px-6 py-8 text-sm text-muted-foreground">
                      No API keys.
                    </p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-6">Name</TableHead>
                          <TableHead className="w-20">Key</TableHead>
                          <TableHead className="w-44">Last used</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usage.keys.map(key => (
                          <TableRow key={key.id}>
                            <TableCell className="pl-6 font-medium">
                              {key.name ?? 'Unnamed'}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {key.keyLastFour
                                ? `…${key.keyLastFour}`
                                : 'Legacy'}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-muted-foreground">
                              {key.lastUsedAt
                                ? formatTimestamp(key.lastUsedAt)
                                : 'Never'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>

            <Card className="w-full">
              <CardContent className="p-0">
                <div className="border-b px-6 py-3 text-sm font-medium">
                  Webhook endpoints
                </div>
                {usage.subscriptions.length === 0 ? (
                  <p className="px-6 py-8 text-sm text-muted-foreground">
                    No webhook subscriptions.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pl-6">Endpoint</TableHead>
                        <TableHead className="w-24">Deliveries</TableHead>
                        <TableHead className="w-24">Success</TableHead>
                        <TableHead className="w-32">Dead-lettered</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {usage.subscriptions.map(subscription => (
                        <TableRow key={subscription.id}>
                          <TableCell
                            className="max-w-[480px] truncate pl-6 font-mono text-sm"
                            title={subscription.url}
                          >
                            {subscription.url}
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {subscription.deliveriesTotal}
                          </TableCell>
                          <TableCell className="tabular-nums">
                            {successRate(
                              subscription.deliveriesSucceeded,
                              subscription.deliveriesTotal,
                            )}
                          </TableCell>
                          <TableCell
                            className={cn(
                              'tabular-nums',
                              subscription.deadLettered === 0 &&
                                'text-muted-foreground/60',
                              subscription.deadLettered > 0 &&
                                'font-medium text-red-700 dark:text-red-400',
                            )}
                          >
                            {subscription.deadLettered}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
